/* eslint-disable max-len */
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import {
  OtpChannel,
  AUTH_CLIENT_SECRET,
  AUTH_API_KEY,
  AUTH_CLIENT_ID,
  AUTH_RESOURCE_TYPE,
  UAE_CODE
} from "../../constants";

import { parseJwt } from "../../utils/genericUtils";
import { DEVICE_ID } from "../../api/axiosConfig";
import { otp, postAuthToken } from "../../api/apiClient";

import {
  getApplicantInfo,
  getProspectId,
  getAuthorizationHeader,
  getIsMobileChanged,
  getIsEmailChanged,
  getSignatories,
  getIsSignatoryPrimaryMobileNoChanged,
  getIsSignatoryPrimaryEmailChanged,
  getIsMultiPartnerJourney
} from "../selectors/appConfig";
import {
  setGeneratingCode,
  generateCodeSuccess,
  verifyCodeSuccess,
  verifyCodeFailed,
  setOtpPendingRequest,
  GENERATE_OTP_CODE,
  VERIFY_OTP,
  maxAttemptsExceeded,
  setVerifiedCredentials
} from "../actions/otp";
import { log } from "../../utils/loggger";
import { NotificationsManager } from "../../components/Notification";
import { getSelectedStakeholder } from "../selectors/stakeholders";
import { setMobileEmailChange, setAccessToken } from "../actions/appConfig";

function* getMobileEmailData(payload = {}) {
  const applicantInfo = yield select(getApplicantInfo);
  const signatoryInfo = yield select(getSignatories);
  const isMobileNumberChangedInPreviewScreen = yield select(getIsMobileChanged);
  const isEmailChangedInPreviousStep = yield select(getIsEmailChanged);
  const isMultiPartnerJourney = yield select(getIsMultiPartnerJourney);

  const isSignatoryPrimaryMobileNoChangedInPreviewScreen = yield select(
    getIsSignatoryPrimaryMobileNoChanged
  );
  const isSignatoryPrimaryEmailChangedInPreviewScreen = yield select(
    getIsSignatoryPrimaryEmailChanged
  );
  const selectedPartner = yield select(getSelectedStakeholder);

  const payloadMobileNo = payload?.mobileNo;
  const payloadEmail = payload?.email;
  const payloadSID = payload?.sid;
  let otpPayload;

  if (isMultiPartnerJourney && selectedPartner && Object.keys(selectedPartner).length > 0) {
    const {
      signatoryId,
      editedMobileNumber,
      editedEmailId,
      contactDetails: {
        primaryEmail,
        primaryMobCountryCode,
        primaryMobileNo,
        editedPrimaryMobileNo,
        editedPrimaryEmailId
      }
    } = selectedPartner;

    otpPayload = {
      mobileNo: isSignatoryPrimaryMobileNoChangedInPreviewScreen
        ? editedPrimaryMobileNo
        : payloadMobileNo || primaryMobileNo,
      email: isSignatoryPrimaryEmailChangedInPreviewScreen
        ? editedPrimaryEmailId
        : payloadEmail || primaryEmail,
      countryCode: primaryMobCountryCode,
      sid: payloadSID || signatoryId
    };
    if (isMobileNumberChangedInPreviewScreen || isEmailChangedInPreviousStep) {
      otpPayload.mobileNo = editedMobileNumber || applicantInfo.mobileNo;
      otpPayload.email = editedEmailId || applicantInfo.email;
      otpPayload.isRequestedByCompany = true;
    } else if (
      isSignatoryPrimaryMobileNoChangedInPreviewScreen ||
      isSignatoryPrimaryEmailChangedInPreviewScreen
    ) {
      otpPayload.email = editedPrimaryEmailId || primaryEmail;
      otpPayload.mobileNo = editedPrimaryMobileNo || primaryMobileNo;
      otpPayload.isRequestedByCompany = false;
    }
    return otpPayload;
  } else {
    const otpMobile = isMobileNumberChangedInPreviewScreen
      ? signatoryInfo[0]?.editedMobileNumber
      : payloadMobileNo || applicantInfo.mobileNo;

    const otpEmail = isEmailChangedInPreviousStep
      ? signatoryInfo[0]?.editedEmailId
      : payloadEmail || applicantInfo.email;

    return {
      mobileNo: otpMobile,
      email: otpEmail,
      countryCode: applicantInfo.countryCode,
      sid: payloadSID
    };
  }
}

export function* showNotification(error) {
  let message = error?.message || "Something went wrong";
  const notificationOptions = { id: "OTP", title: "Warning", message, delay: 5000 };
  NotificationsManager.add(notificationOptions);
  yield put(maxAttemptsExceeded(message));
}

export function* generateOtp(action) {
  try {
    const headers = yield select(getAuthorizationHeader);
    const headersWithViewId = {
      headers: {
        ...headers.headers,
        "stage-id": "generateOtp"
      }
    };
    const prospectIdFromState = yield select(getProspectId);
    const prospectId = prospectIdFromState || action.payload?.prospectId;
    const { mobileNo, email, sid, isRequestedByCompany } = yield call(
      getMobileEmailData,
      action.payload
    );
    yield call(
      otp.generate,
      { ...action.payload, prospectId, mobileNo, email, sid, isRequestedByCompany },
      headersWithViewId
    );
    yield put(generateCodeSuccess());
  } catch (error) {
    log(error);
    yield put(setGeneratingCode(false));
    yield put(verifyCodeFailed(false));
    yield call(showNotification, error);
  }
}

const getMobileEmailChangePayload = (isRequestedByCompany, mode) => {
  if (!isRequestedByCompany) {
    return mode === OtpChannel.Sms
      ? { isSignatoryMobileNumberChanged: false }
      : { isSignatoryEmailIdChanged: false };
  } else {
    return mode === OtpChannel.Sms ? { isMobileNumberChanged: false } : { isEmailIdChanged: false };
  }
};

export function* verifyOtp(action) {
  try {
    const headers = yield select(getAuthorizationHeader);
    const headersWithViewId = {
      headers: {
        ...headers.headers,
        "stage-id": "verifyOtp"
      }
    };
    const { code, mode } = action.payload;
    const storedProspectId = yield select(getProspectId);
    const prospectId = storedProspectId || action.payload?.prospectId;
    const { mobileNo, email, countryCode, sid, isRequestedByCompany } = yield call(
      getMobileEmailData,
      action.payload
    );
    const { deviceId: uniqueId = "" } = parseJwt(headers.headers.Authorization);
    const payload = {
      email,
      prospectId,
      mobileNo,
      countryCode,
      otpToken: code,
      mode,
      sid,
      isRequestedByCompany,
      deviceId: DEVICE_ID,
      uniqueId
    };
    if (!prospectId) {
      delete payload.prospectId;
    }
    const { data } = yield call(otp.verify, payload, headersWithViewId);
    if (data.verified) {
      const changePayload = getMobileEmailChangePayload(isRequestedByCompany, mode);
      yield put(setMobileEmailChange(changePayload));
      yield put(setVerifiedCredentials(payload));
      if (mode === "email" && payload.countryCode === UAE_CODE) {
        //added countrycode check, beacuse getting resource_id mandatory error for token call in case of non UAE numbers
        const authTokenData = {
          client_id: AUTH_CLIENT_ID,
          resource_type: AUTH_RESOURCE_TYPE,
          client_secret: AUTH_CLIENT_SECRET,
          device_id: DEVICE_ID,
          apiKey: AUTH_API_KEY,
          unique_id: uniqueId,
          ...(sid && { sub_resource_id: sid })
        };
        const { data } = yield call(postAuthToken.get, authTokenData, headers);
        yield put(setAccessToken(data.access_token, data.refresh_token));
      }
      yield put(verifyCodeSuccess());
    } else {
      yield put(verifyCodeFailed());
    }
  } catch (error) {
    log(error);
    yield call(showNotification, error);
  } finally {
    yield put(setOtpPendingRequest(false));
  }
}

export default function* otpSagas() {
  yield all([takeLatest(GENERATE_OTP_CODE, generateOtp), takeLatest(VERIFY_OTP, verifyOtp)]);
}
